import React, { JSX, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Spin, Tabs, TabsProps } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Dynamics } from '@/services/dynamics/dynamics';
import { logger } from '@/services/logger.service';
import {
  DynAssignToData,
  DynRelatedToData,
} from '@/services/dynamics/interfaces/relate-to-info.interface';
import { EEntity } from '@/services/dynamics/enums/entity.enum';
import { getRegionByPhoneNumber } from '@/services/phoneRegionLocator/getRegionByPhoneNumber';
import { SolgariApiService } from '@/services/solgari-api/solgari-api.service';
import { phoneCallApiMapper } from '@/services/dynamics/mappers/phone-call.mapper';
import { NoteReduced } from '@/components/Note/NoteReduced';
import { MAIN } from '@/configs/main.config';
import { RecordPlayer } from '@/components/RecordPlayer/RecordPlayer';
import {
  AccountInfo,
  AccountInfoProps,
} from '@/components/AccountInfo/AccountInfo';
import SolgariCopilot from '@/components/SolgariCopilot/SolgariCopilot';
import { useStore } from '@/stores';
import { getFullName } from '@/shared/helpers/full-name-parser';
import { RemoteUserType } from '@/services/sip-js.service';
import { USER_TYPE } from '@/shared/constants/user';
import './CallRecord.scss';

export interface FakeTimer {
  time?: any;
  minutes: number;
}

export interface INoteState {
  id: string;
  text: string;
  relatedToData: DynRelatedToData;
  assignToData: DynAssignToData;
}

export function addPlus(str: string) {
  if (!str) return str;
  return str.includes('+') ? str : '+' + str;
}

export const CallRecord = () => {
  const {
    transcriptStore: { getCallSessionTranscript },
  } = useStore();
  const [isLoaded, setIsLoaded] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const [note, setNote] = useState<INoteState>({
    id: '',
    text: '',
    relatedToData: null,
    assignToData: null,
  });
  const [audioUrl, setAudioUrl] = useState<string>();
  const [accountInfo, setAccountInfo] = useState<AccountInfoProps>();
  const [location, setLocation] = useState<string>('');

  const routeLocation = useLocation();

  const callRecordId = routeLocation.state.id;
  const antIcon = <LoadingOutlined spin />;

  useEffect(() => {
    getRecording();

    return () => {
      setSessionId('');
    };
  }, [callRecordId]);

  const items = (
    first: JSX.Element,
    second: JSX.Element,
  ): TabsProps['items'] => {
    return [
      { label: 'Note', children: first, key: '1' },
      {
        label: 'Solgari Copilot',
        children: second,
        key: '2',
        disabled: !sessionId,
      },
    ];
  };

  const savePhoneCall = async ({
    noteId,
    noteText,
    noteRelatedInfo,
  }: {
    noteId: string;
    noteText: string;
    noteRelatedInfo: DynRelatedToData;
  }): Promise<string> => {
    const { id } = await Dynamics.savePhoneCallNote({ desc: noteText, noteId });

    setNote({
      id,
      text: noteText,
      relatedToData: noteRelatedInfo,
      assignToData: note.assignToData,
    });

    return id;
  };

  const getRecording = async () => {
    const recording = await Dynamics.retrieveCallRecord({
      entityLogicalName: EEntity.PHONE_CALL,
      id: callRecordId,
      options: '?$select=*',
    });

    if (!recording) {
      logger.error(`[DYNAMICS] Can not get a recording by id: ${callRecordId}`);
      return;
    }

    const callNote = phoneCallApiMapper(recording);

    setAccountInfo({
      id: recording._regardingobjectid_value,
      fullName: getFullName(callNote.fullName, callNote.subject),
      phone: recording.phonenumber,
      entity:
        recording[
          '_regardingobjectid_value@Microsoft.Dynamics.CRM.lookuplogicalname'
        ],
    });

    const agents = await SolgariApiService.getListOfAgents();
    const isInternal = agents.some(
      (agent) => agent.extensionId.toString() === recording.phonenumber,
    );
    const userType: RemoteUserType = isInternal
      ? USER_TYPE.INTERNAL
      : USER_TYPE.EXTERNAL;

    if (userType === USER_TYPE.EXTERNAL) {
      setLocation(getRegionByPhoneNumber(addPlus(recording.phonenumber)));
    }

    setNote({
      id: recording.activityid,
      text: recording.description,
      relatedToData: {
        id: recording._regardingobjectid_value,
        type: recording[
          '_regardingobjectid_value@Microsoft.Dynamics.CRM.lookuplogicalname'
        ],
      },
      assignToData: note.assignToData,
    });

    const tenantId = localStorage.getItem(MAIN.lsTenantIdKey);
    const sessionId = recording.activityadditionalparams;
    setSessionId(sessionId);

    SolgariApiService.getRecordingUri(sessionId, tenantId)
      .then((audioUrl) => {
        setAudioUrl(audioUrl);
        setIsLoaded(true);
      })
      .catch((error) => logger.error(`[Api] Can't get recording uri: `, error));
  };

  const onTabChange = (key: string) => {
    if (key === '2') {
      getCallSessionTranscript(sessionId);
    }
  };

  return (
    <div className="call-record">
      {isLoaded ? (
        <>
          <div className="call-record__header">
            <div className="call-record__account">
              <AccountInfo
                entity={accountInfo.entity}
                id={accountInfo.id}
                fullName={accountInfo.fullName}
                phone={accountInfo.phone}
                size={40}
              />
              {location && (
                <span className="call-record__location">{location}</span>
              )}
            </div>
            <div className="call-record__player">
              <RecordPlayer audioUrl={audioUrl} />
            </div>
          </div>
          <div className="call-record__body">
            <Tabs
              defaultActiveKey="1"
              items={items(
                <NoteReduced
                  note={note}
                  savePhoneCallHandler={savePhoneCall}
                />,
                <SolgariCopilot></SolgariCopilot>,
              )}
              onChange={onTabChange}
            />
          </div>
        </>
      ) : (
        <Spin className="spinner" indicator={antIcon} />
      )}
    </div>
  );
};
